import { ConsentStatus, ConsentWorkflow, IEConsentForm } from "@/api/dtos";
import EConsentContext, { EConsentUserType, IEConsentContext } from "@/contexts/EConsentContext";
import useInvestigators from "@/hooks/data/useInvestigators";
import usePrincipalInvestigator from "@/hooks/data/usePrincipalInvestigator";
import { getEConsentFormRegistration } from "@/components/consent/eConsentFormRegistry";
import { Alert, AlertTitle } from "@mui/material";
import {
    AuthenticatedUserContext,
    FormContext,
    FormDefinitionContext,
    IFormContext,
    InstitutionContext,
    PatientContext,
    RouteLoading
} from "@ngt/opms";
import { FC, PropsWithChildren, useContext, useMemo } from "react";

const InvestigatorEConsentProvider: FC<PropsWithChildren> = ({ children }) => {
    const { data: form } = useContext(FormContext) as unknown as IFormContext<IEConsentForm>;
    const { data: institution } = useContext(InstitutionContext);
    const { data: patient } = useContext(PatientContext);
    const { data: formDefinition } = useContext(FormDefinitionContext);
    const { data: authenticatedUser } = useContext(AuthenticatedUserContext);
    const investigators = useInvestigators(institution?.code);
    const principalInvestigator = usePrincipalInvestigator(institution?.code);
    const registration = getEConsentFormRegistration(formDefinition?.code);

    const assignedUsername = form?.assignedInvestigatorUsername?.trim();
    const authenticatedUsername = authenticatedUser?.userName?.trim();
    const assignedToCurrentUser = !!assignedUsername && !!authenticatedUsername &&
        assignedUsername.localeCompare(authenticatedUsername, undefined, { sensitivity: "accent" }) === 0;
    const validStatus = form?.consentStatus === ConsentStatus.AwaitingInvestigatorSignature ||
        form?.consentStatus === ConsentStatus.Completed;

    const value = useMemo<IEConsentContext>(() => ({
        investigators: investigators.data,
        principalInvestigator: principalInvestigator.data,
        userType: EConsentUserType.Investigator,
        participantName: [form?.participantFirstName, form?.participantLastName].filter(Boolean).join(" "),
        picfDownloadUrl: institution?.code && form?.picfId && registration?.picfTypeId
            ? `/api/GetDownloadPicf?${new URLSearchParams({
                InstCode: institution.code,
                PicfId: String(form.picfId),
                PicfTypeId: String(registration.picfTypeId)
            })}`
            : undefined,
        picfVersion: form?.picfVersion,
        readOnly: form?.consentStatus === ConsentStatus.Completed,
        consentStatus: form?.consentStatus
    }), [form, institution?.code, investigators.data, principalInvestigator.data, registration?.picfTypeId]);

    if (!form || !patient) {
        return <RouteLoading />;
    }

    if (form.workflow !== ConsentWorkflow.EConsent || !validStatus) {
        return (
            <Alert severity="warning">
                <AlertTitle>eConsent unavailable</AlertTitle>
                This form is not currently available for investigator eConsent completion.
            </Alert>
        );
    }

    if (!assignedToCurrentUser) {
        return (
            <Alert severity="error">
                <AlertTitle>eConsent access denied</AlertTitle>
                Only the assigned investigator can access this eConsent form.
            </Alert>
        );
    }

    return (
        <EConsentContext.Provider value={value}>
            {children}
        </EConsentContext.Provider>
    );
};

export default InvestigatorEConsentProvider;
